import { Divider, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography } from '@mui/material';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useLocation, useNavigate } from 'react-router';
import { FEATURE_FLAG } from '../../../constants/featureFlags';
import useLocalization from '../../../hooks/useLocalization';
import useProductAvailability from '../../../hooks/useProductAvailability';
import { useSports } from '../../../queries';
import { mergeSx } from '../../../utils/styles';
import PromoIcon from '../../icons/PromoIcon';
import SportListItem from './SportListItem';
import SportListSkeleton from '../skeletons/SportListSkeleton';

const styles = {
  list: {
    py: 0,
  },
  sectionHeader: {
    py: 1,
  },
  sectionTitle: {
    color: 'info.main',
    textTransform: 'uppercase',
  },
  promotionsSectionDivider: {
    my: 0.5,
    borderColor: 'divider',
  },
  promotionsListItemButton: {
    borderRadius: 1,
  },
  promotionsListItemButtonActive: {
    backgroundColor: 'neutral.700',
    '& .MuiListItemText-primary': {
      fontWeight: 700,
    },
  },
  promotionsIcon: {
    minWidth: 32,
    color: 'text.secondary',
  },
  promotionsIconActive: {
    color: 'info.main',
  },
} as const;

type BettingDrawerContentProps = {
  onNavigate?: () => void;
};

const BettingDrawerContent = ({ onNavigate }: BettingDrawerContentProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLocalization();
  const { hasSportsbookAccess } = useProductAvailability();
  const promotionsEnabled = useFeatureIsOn(FEATURE_FLAG.PROMOTIONS_ENABLED);
  const isPromotionsSelected = location.pathname.startsWith('/promotions');

  const {
    data: sportsData,
    isLoading,
    isSuccess,
  } = useSports({
    enabled: hasSportsbookAccess,
    queryParams: {
      isLive: false,
    },
  });

  const navigateTo = (id: string, name: string) => {
    navigate(`/sports/${id}`, {
      state: {
        name,
      },
    });
  };

  const navigateToPromotions = () => {
    navigate('/promotions');
    onNavigate?.();
  };

  if (!hasSportsbookAccess) return null;

  return (
    <List sx={styles.list}>
      {promotionsEnabled && (
        <>
          <ListItemButton
            onClick={navigateToPromotions}
            sx={mergeSx(styles.promotionsListItemButton, isPromotionsSelected && styles.promotionsListItemButtonActive)}
          >
            <ListItemIcon sx={mergeSx(styles.promotionsIcon, isPromotionsSelected && styles.promotionsIconActive)}>
              <PromoIcon fontSize='small' />
            </ListItemIcon>
            <ListItemText primary={t('promotions', 'Promotions')} />
          </ListItemButton>
          <Divider sx={styles.promotionsSectionDivider} />
        </>
      )}

      <ListItem sx={styles.sectionHeader} data-testid='sports-list-section-title'>
        <Typography variant='h5' sx={styles.sectionTitle}>
          {t('fullList')}
        </Typography>
      </ListItem>
      {isLoading ? (
        <SportListSkeleton itemCount={20} />
      ) : (
        isSuccess && sportsData.map((sport) => <SportListItem key={sport.id} sport={sport} onClick={navigateTo} />)
      )}
    </List>
  );
};

export default BettingDrawerContent;
